import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import { merge, Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import {
  buildUpdatePatientContactsAndConsentRequest,
  buildUpdatePatientNameRequest,
  mapPatientContacts,
  PatientContacts,
  PatientDetailsFormValues,
} from '../../models';
import { ApiService, SentryService } from '../../services';
import { FetchPatientContacts, SetPatientContacts, UpdatePatientDetails } from './contacts.actions';
import { SetPatientConsent, SetPatientName, UpdatePatient } from '../patient/patient.actions';
import { PatientState, PatientStateModel } from '../patient/patient.state';
import {
  ApiStoreData,
  defaultApiStoreData,
  CcsSentryError,
  InvalidApiResponseDataError,
  MissingApiRequestDataError,
} from '../utils';

export interface ContactsStateModel {
  contacts: ApiStoreData<PatientContacts | null>;
  updatePatientName: ApiStoreData<null>;
  updatePatientContactsAndConsent: ApiStoreData<null>;
}

@State<ContactsStateModel>({
  name: 'patientContacts',
  defaults: {
    contacts: defaultApiStoreData,
    updatePatientName: defaultApiStoreData,
    updatePatientContactsAndConsent: defaultApiStoreData,
  },
})
@Injectable({ providedIn: 'root' })
export class ContactsState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static patientContactsState(state: ContactsStateModel): ApiStoreData<PatientContacts | null> {
    return state.contacts;
  }

  @Selector([PatientState])
  static isUpdatingPatientDetails(state: ContactsStateModel, patientState: PatientStateModel): boolean {
    return (
      state.updatePatientName.isLoading ||
      state.updatePatientContactsAndConsent.isLoading ||
      patientState.updatePatient.isUpdating
    );
  }

  @Selector([PatientState])
  static updatePatientDetailsError(
    state: ContactsStateModel,
    patientState: PatientStateModel
  ): CcsSentryError[] | null {
    const errors = [
      state.updatePatientName.error,
      state.updatePatientContactsAndConsent.error,
      patientState.updatePatient.error,
    ].filter((e) => !!e);

    return errors.length === 0 ? null : (errors as CcsSentryError[]);
  }

  @Selector([PatientState])
  static isUpdatingPatientDetailsFinished(state: ContactsStateModel, patientState: PatientStateModel): boolean {
    return (
      state.updatePatientName.isFinished &&
      state.updatePatientContactsAndConsent.isFinished &&
      patientState.updatePatient.isFinished
    );
  }

  @Action(FetchPatientContacts)
  fetchPatientContacts(ctx: StateContext<ContactsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.contacts.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'contacts' });
    }

    return this.apiService.getPatientContacts(patientId).pipe(
      tap({
        next: (response) => {
          const data = mapPatientContacts(response.data);
          if (!data.primaryEmail) {
            const error = new InvalidApiResponseDataError(['Missing primary patient email']);
            this.handleError({ ctx, error, stateKey: 'contacts' });
          } else if (!data.primaryPhone) {
            const error = new InvalidApiResponseDataError(['Missing primary patient phone']);
            this.handleError({ ctx, error, stateKey: 'contacts' });
          } else {
            this.handleSuccess({ ctx, data, stateKey: 'contacts' });
          }
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'contacts' }))
    );
  }

  updatePatientName(ctx: StateContext<ContactsStateModel>, formValues: PatientDetailsFormValues) {
    ctx.setState(
      produce((draft) => {
        draft.updatePatientName.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'updatePatientName' });
    }

    const body = buildUpdatePatientNameRequest(patientId, formValues);

    return this.apiService.updatePatientName(patientId, body).pipe(
      tap({
        next: (response) => {
          this.store.dispatch(new SetPatientName(response.data[0])).subscribe(() => {
            this.handleSuccess({ ctx, stateKey: 'updatePatientName' });
          });
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          this.handleError({ ctx, error, stateKey: 'updatePatientName', resetData: false })
      )
    );
  }

  updatePatientContactsAndConsent(ctx: StateContext<ContactsStateModel>, formValues: PatientDetailsFormValues) {
    ctx.setState(
      produce((draft) => {
        draft.updatePatientContactsAndConsent.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'updatePatientContactsAndConsent' });
    }

    const body = buildUpdatePatientContactsAndConsentRequest(patientId, formValues, ctx.getState().contacts.data);

    return this.apiService.updatePatientContactsAndConsent(patientId, body).pipe(
      tap({
        next: (response) => {
          this.store
            .dispatch([
              new SetPatientContacts(response.data),
              new SetPatientConsent(patientId, formValues.consentedToMarketing),
            ])
            .subscribe(() => {
              this.handleSuccess({ ctx, stateKey: 'updatePatientContactsAndConsent' });
            });
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          this.handleError({ ctx, error, stateKey: 'updatePatientContactsAndConsent', resetData: false })
      )
    );
  }

  @Action(UpdatePatientDetails)
  updatePatientDetails(ctx: StateContext<ContactsStateModel>, action: UpdatePatientDetails) {
    return merge(
      this.store.dispatch(
        new UpdatePatient({
          refusedGenericMedications: action.patientDetailsFormValues.refusedGenericMedications,
        })
      ),
      this.updatePatientName(ctx, action.patientDetailsFormValues),
      this.updatePatientContactsAndConsent(ctx, action.patientDetailsFormValues)
    );
  }

  @Action(SetPatientContacts)
  setPatientContacts(ctx: StateContext<ContactsStateModel>, action: SetPatientContacts) {
    ctx.setState(
      produce((draft) => {
        draft.contacts.data = mapPatientContacts(action.apiContacts);
      })
    );
  }
}
